// ===========================================
// COMPONENTE: RepartoConceptos — ORR-230 fase 3
// ===========================================
// Reparte los conceptos de un cliente entre cualquiera del estudio (ORR-303). Delegar un concepto
// NO delega el cliente: el asignado ve y marca presentado ese vencimiento, y el nombre del cliente.

import { useMemo } from 'react'
import { Users } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { usePermissions } from '@/hooks/usePermissions'
import { useUsuariosOrg } from '@/hooks/useUsuarios'
import {
  useConceptosCliente,
  useAsignarConcepto,
  useDesasignarConcepto,
} from '@/core/hooks/useConceptos'
import type { VencimientoConCliente } from '@/types/vencimientos'
import {
  derivarConceptos,
  opcionesDeReparto,
  candidatosDeReparto,
  claveConcepto,
  type ConceptoDelCliente,
} from './conceptosDelCliente'

interface RepartoConceptosProps {
  clienteId: string
  vencimientos: VencimientoConCliente[]
}

export function RepartoConceptos({ clienteId, vencimientos }: RepartoConceptosProps) {
  const { puede } = usePermissions()
  const { data: usuarios = [], isLoading: cargandoUsuarios } = useUsuariosOrg()
  const {
    data: asignaciones = [],
    isLoading: cargandoAsignaciones,
    isError,
  } = useConceptosCliente(clienteId)
  const asignar = useAsignarConcepto()
  const desasignar = useDesasignarConcepto()

  const conceptos = useMemo(() => derivarConceptos(vencimientos), [vencimientos])

  // Cualquiera del estudio puede recibir un concepto (ORR-303): limitarlo a los responsables del
  // cliente anulaba el sentido de la función, que es delegar un impuesto sin delegar el cliente.
  // Sigue el filtro por `activo`: a un usuario bloqueado se le podrían asignar conceptos que nunca
  // va a ver.
  const candidatos = useMemo(() => candidatosDeReparto(usuarios), [usuarios])

  const opciones = useMemo(
    () => opcionesDeReparto(candidatos, asignaciones),
    [candidatos, asignaciones],
  )

  if (conceptos.length === 0) return null

  // Mientras no se sepa qué hay repartido no se pinta nada: el `[]` de una query pendiente diría
  // "Todos" y habilitaría toggles que decidirían sobre un estado que nunca fue cierto.
  if (cargandoUsuarios || cargandoAsignaciones) return null

  if (isError) {
    return (
      <div className="rounded-lg border p-3">
        <p className="text-sm font-medium">Reparto entre responsables</p>
        <p className="text-xs text-muted-foreground">
          No se pudo cargar el reparto. Volvé a abrir el cliente para reintentar.
        </p>
      </div>
    )
  }

  // Con una sola persona en el estudio no hay nada que repartir, salvo que hayan quedado
  // asignaciones de alguien que después fue bloqueado: esas hay que poder limpiarlas.
  if (candidatos.length < 2 && asignaciones.length === 0) return null

  const guardando = asignar.isPending || desasignar.isPending

  const alternar = (concepto: ConceptoDelCliente, usuarioId: string) => {
    const existente = asignaciones.find(
      (a) => a.usuarioId === usuarioId && a.tipo === concepto.tipo && a.conceptoId === concepto.id,
    )

    if (existente) {
      desasignar.mutate({ id: existente.id, clienteId })
      return
    }

    asignar.mutate({
      clienteId,
      usuarioId,
      impuestoId: concepto.tipo === 'impuesto' ? concepto.id : null,
      obligacionRecurrenteId: concepto.tipo === 'obligacion' ? concepto.id : null,
      // Solo para la notificación: no viajan a la base.
      usuarioEmail: candidatos.find((c) => c.id === usuarioId)?.email,
      conceptoNombre: concepto.nombre,
      clienteNombre: vencimientos[0]?.cliente_nombre ?? 'un cliente',
    })
  }

  return (
    <div className="rounded-lg border p-3 space-y-3">
      <div>
        <p className="text-sm font-medium">Reparto de conceptos</p>
        <p className="text-xs text-muted-foreground">
          Un concepto sin nadie asignado queda a cargo de todos los responsables del cliente.
          Asignárselo a alguien más no le da acceso al resto del cliente.
        </p>
      </div>

      <div className="space-y-2">
        {conceptos.map((concepto) => {
          const asignados = asignaciones.filter(
            (a) => a.tipo === concepto.tipo && a.conceptoId === concepto.id,
          )

          return (
            <div
              key={claveConcepto(concepto.tipo, concepto.id)}
              className="flex items-center justify-between gap-2"
            >
              <span className="text-sm truncate">{concepto.nombre}</span>

              <div className="flex items-center justify-end gap-1 flex-wrap">
                {asignados.length === 0 ? (
                  <Badge variant="outline" className="text-xs">
                    Todos
                  </Badge>
                ) : (
                  asignados.map((a) => (
                    <Badge key={a.id} variant="secondary" className="text-xs">
                      {a.usuarioNombre}
                    </Badge>
                  ))
                )}

                {puede.asignarConceptos && (
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 px-2"
                        disabled={guardando}
                        aria-label={`Elegir a cargo de ${concepto.nombre}`}
                      >
                        <Users className="h-3.5 w-3.5" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent align="end" className="w-56 p-2">
                      {opciones.map((o) => (
                        <label
                          key={o.id}
                          className="flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground"
                        >
                          <Checkbox
                            checked={asignados.some((a) => a.usuarioId === o.id)}
                            disabled={guardando}
                            onCheckedChange={() => alternar(concepto, o.id)}
                          />
                          <span className="truncate">
                            {o.nombre}
                            {!o.vigente && (
                              <span className="text-muted-foreground"> · ya no está activo</span>
                            )}
                          </span>
                        </label>
                      ))}
                    </PopoverContent>
                  </Popover>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
